import {gql, useQuery} from '@apollo/client';
import ProgressBar from "@badrap/bar-of-progress";
import {ProductTypeEnum} from "API";
import Loader from 'components/Loader/Loader';
import ProductCard from 'components/ProductCard/ProductCard';
import NoResults from "components/shop/NoResults";
import {ModalContext} from "contexts/modal.provider";
import {SearchContext} from "contexts/search/search.provider";
import {getProductImpressions} from "helper/googleTags";
import {useRouter} from "next/router";
import React, {useContext, useEffect, useState} from 'react';
import InfiniteScroll from 'react-infinite-scroller';

import {listProductsByCategory, searchProducts} from "../../graphql/query/product.query";
import QuickView from '../QuickView/QuickView';
import {ProductCardWrapper, ProductsCol, ProductsRow,} from './Products.style';

const progress = new ProgressBar({
    size: 2,
    color: '#f15331',
    className: 'bar-of-progress',
    delay: 100,
});


type ProductsProps = {
    deviceType?: {
        mobile: boolean;
        tablet: boolean;
        desktop: boolean;
    };
    category?: string;
    fetchLimit?: number;
    loadMore?: boolean;
    initialProducts?: any[];
};
export const Products: React.FC<ProductsProps> = ({
                                                      deviceType,
                                                      category,
                                                      fetchLimit = 24,
                                                      loadMore = true,
                                                      initialProducts = [],
                                                      ...rest
                                                  }) => {
    const router = useRouter();
    const [gtm, setGtm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currCat, setCurrCat] = useState(category);
    const {openModal, closeModal} = useContext(ModalContext);
    const {search, setSearch} = useContext(SearchContext);
    const [nextToken, setNextToken] = useState(null);
    const [searchState, setSearchState] = useState({
        nextToken: null,
        category: null,
        text: null,
    });
    const [loadMoreToken, setLoadMoreToken] = useState(null);
    const [searchLoadMoreToken, setSearchLoadMoreToken] = useState(null);


    useEffect(() => {
        if (currCat !== router.query?.category) {
            setNextToken(null);
            setLoadMoreToken(null);
            setSearchLoadMoreToken(null);
        }
    }, [router.query, currCat]);

    const {data, loading: productsLoading, refetch} = useQuery(gql(listProductsByCategory), {
        skip: search || category,
        notifyOnNetworkStatusChange: true,
        variables: {
            company: 'scuttle',
            limit: 500,
            nextToken,
        },
    });


    const listProductsByCategoryRequest = useQuery(gql(listProductsByCategory), {
        skip: !category,
        variables: {
            company: 'scuttle',
            categoryIdSeq: {
                beginsWith: {
                    categoryId: `${category}`,
                }
            },
            limit: fetchLimit,
            nextToken
        },
    });


    const searchProductRequest = useQuery(gql(searchProducts), {
        context: {clientName: "unauthed"},
        skip: !search?.text,
        variables: {
            sort: {
                field: "id",
                direction: "asc"
            },
            filter: {
                and: [
                    {company: {eq: 'scuttle'}},
                    {name: {matchPhrasePrefix: search?.text}},
                    {type: {ne: ProductTypeEnum.PVT_ITEM}},
                ]
            },
            limit: fetchLimit,
            nextToken: searchState.nextToken,
        },
    });


    const [products, setProducts] = useState([
        ...listProductsByCategoryRequest?.data?.listProductsByCategory.items.map(p => p.product).filter(p => p) || [],
        ...data?.listProductsByCategory?.items.map(p => p.product).filter(p => p) || [],
    ]);

    useEffect(() => {
        const load = productsLoading || searchProductRequest.loading || listProductsByCategoryRequest.loading;
        setLoading(load);

        if (searchProductRequest.loading) {
            setLoadMoreToken(null);
        }

        if (load) {
            progress.start();
        } else {
            progress.finish();
        }
        if (productsLoading) {
            setSearchLoadMoreToken(null);
        }

    }, [productsLoading, searchProductRequest, listProductsByCategoryRequest]);

    useEffect(() => {
        if (!productsLoading && data) {
            setProducts([...products, ...data.listProductsByCategory.items.map(p => p.product).filter(p => p)]);
            setLoadMoreToken(data.listProductsByCategory.nextToken);
        }
    }, [productsLoading, data]);


    useEffect(() => {
        if (products.length > 0) {
            (window as any).dataLayer?.push(getProductImpressions(products))
        }
    }, [products]);


    useEffect(() => {
        if (!listProductsByCategoryRequest.loading && listProductsByCategoryRequest.data) {
            const {items, nextToken} = listProductsByCategoryRequest.data.listProductsByCategory;

            if (currCat === category) {
                setProducts([...products, ...items.map(p => p.product).filter(p => p)]);
            } else {
                setProducts([...items.map(p => p.product).filter(p => p)]);
            }
            setCurrCat(category);
            setLoadMoreToken(nextToken);
        }
    }, [listProductsByCategoryRequest]);


    useEffect(() => {
        if (!searchProductRequest.loading && searchProductRequest.data) {

            const {items, nextToken, total} = searchProductRequest.data.searchProducts;

            if (searchState.nextToken && searchState.text === search?.text) {
                setProducts([...products, ...items]);
            } else {
                setProducts(items);
            }
            if (items.length === fetchLimit) {
                setSearchLoadMoreToken(nextToken);
            } else {
                setSearchLoadMoreToken(null);
            }
        }
    }, [searchProductRequest]);


    const showItemModal = (item) => {
        openModal({
            fullscreen: true,
            content: <QuickView
                item={item}
                onModalClose={closeModal}
            />
        });
    }

    const handleLoadMore = () => {
        if (search?.text) {
            setSearchState({
                ...searchState,
                nextToken: searchLoadMoreToken
            });
        } else {
            setNextToken(loadMoreToken);
        }
    };

    function onClearSearch() {
        setSearch({text: null});
        listProductsByCategoryRequest?.refetch();
        refetch();
    }

    return (
        <>
            <InfiniteScroll
                pageStart={0}
                loadMore={handleLoadMore}
                hasMore={loadMore && (loadMoreToken || searchLoadMoreToken)}
                loader={<div className="flex justify-center"><Loader color='#009E7F'/></div>}
            >
                {
                    !loading && products.length === 0 ?

                        <NoResults onClearSearch={onClearSearch}/>


                        :
                        <>
                            <ProductsRow>

                                {products
                                    .filter(p => p?.type !== ProductTypeEnum.PVT_ITEM)
                                    .map((item: any, index: number) => {
                                        return (
                                            <ProductsCol key={index}>
                                                <ProductCardWrapper>
                                                    <ProductCard
                                                        key={`${index}${item.id}`}
                                                        product={item}
                                                        onClick={() =>
                                                            showItemModal(item)
                                                        }
                                                    />
                                                </ProductCardWrapper>
                                            </ProductsCol>
                                        )
                                    })}

                            </ProductsRow>
                        </>
                }
            </InfiniteScroll>
            {gtm}
        </>
    );
};
export default Products;
